<script setup lang="ts">
import type { IngestReportSummary, KnowledgeDoc, KnowledgeDocSummary } from "@maimu/contracts";
import { computed, onMounted, ref } from "vue";

import { api } from "@/lib/api.js";

/**
 * The knowledge half of the admin panel (D18).
 *
 * Two kinds of document live in one list. Files come from `core/knowledge` in git and
 * are read-only here — saving one writes a database document that *shadows* it, which
 * is the only honest thing the panel can do to a read-only mount. Database documents
 * are the panel's own, and deleting one that shadows a file is a revert rather than a
 * deletion.
 *
 * The export line is shown, not hidden in the docs: an edit that never reaches
 * `pnpm kb:export-docs` never reaches git or maimu-claw, and the two interfaces
 * quietly stop being the same assistant.
 */
const docs = ref<KnowledgeDocSummary[]>([]);
const selected = ref<KnowledgeDoc | null>(null);
const creating = ref(false);
const loading = ref(true);
const busy = ref(false);
const error = ref<string | null>(null);
const notice = ref<string | null>(null);
const report = ref<IngestReportSummary | null>(null);

const draft = ref({ path: "", source: "", language: "et", quality: 1, body: "" });

const ORIGIN_LABELS = { file: "failis", database: "andmebaasis", override: "ülekirjutus" } as const;

const editingFile = computed(() => selected.value?.origin === "file");
const canDelete = computed(() => selected.value !== null && selected.value.origin !== "file");

function fail(cause: unknown, fallback: string): void {
  error.value = cause instanceof Error ? cause.message : fallback;
}

async function load(): Promise<void> {
  loading.value = true;
  try {
    docs.value = await api.knowledgeDocs();
  } catch (cause) {
    fail(cause, "Dokumente ei õnnestunud laadida.");
  } finally {
    loading.value = false;
  }
}

async function open(path: string): Promise<void> {
  error.value = null;
  notice.value = null;
  creating.value = false;
  try {
    const doc = await api.knowledgeDoc(path);
    selected.value = doc;
    draft.value = {
      path: doc.path,
      source: doc.source,
      language: doc.language,
      quality: doc.quality,
      body: doc.body,
    };
  } catch (cause) {
    fail(cause, "Dokumenti ei õnnestunud avada.");
  }
}

function startNew(): void {
  error.value = null;
  notice.value = null;
  creating.value = true;
  selected.value = null;
  draft.value = { path: "", source: "", language: "et", quality: 1, body: "" };
}

function close(): void {
  creating.value = false;
  selected.value = null;
}

async function save(): Promise<void> {
  busy.value = true;
  error.value = null;
  notice.value = null;
  try {
    const result = await api.saveKnowledgeDoc({ ...draft.value, quality: Number(draft.value.quality) });
    notice.value = result.indexWarning ?? `Salvestatud ja indekseeritud (${result.chunks} lõiku).`;
    await load();
    await open(draft.value.path);
  } catch (cause) {
    fail(cause, "Salvestamine ebaõnnestus.");
  } finally {
    busy.value = false;
  }
}

async function remove(): Promise<void> {
  const doc = selected.value;
  if (!doc) return;

  const question =
    doc.origin === "override"
      ? `Kustutada ülekirjutus ja taastada failiversioon "${doc.path}"?`
      : `Kustutada dokument "${doc.path}" jäädavalt?`;
  if (!confirm(question)) return;

  busy.value = true;
  error.value = null;
  try {
    const result = await api.deleteKnowledgeDoc(doc.path);
    await load();
    if (result.doc) {
      await open(doc.path);
      notice.value = `Ülekirjutus kustutatud — failiversioon on taas kasutusel (${result.chunks} lõiku).`;
    } else {
      close();
      notice.value = "Dokument on kustutatud ja teadmusest eemaldatud.";
    }
  } catch (cause) {
    fail(cause, "Kustutamine ebaõnnestus.");
  } finally {
    busy.value = false;
  }
}

async function reingest(): Promise<void> {
  busy.value = true;
  error.value = null;
  notice.value = null;
  report.value = null;
  try {
    report.value = await api.reingestKnowledge();
    await load();
  } catch (cause) {
    fail(cause, "Indekseerimine ebaõnnestus.");
  } finally {
    busy.value = false;
  }
}

onMounted(load);
</script>

<template>
  <section class="kb">
    <header class="kb-head">
      <p class="hint">
        Failipõhised dokumendid tulevad gitist. Siin salvestamine loob andmebaasi ülekirjutuse — see kehtib kohe, aga
        gitti ja maimu-clawni jõuab see alles käsuga <code>pnpm kb:export-docs</code>.
      </p>
      <div class="tools">
        <button class="btn" type="button" :disabled="busy" @click="startNew()">Uus dokument</button>
        <button class="btn-ghost" type="button" :disabled="busy" @click="reingest()">Indekseeri uuesti</button>
      </div>
    </header>

    <p v-if="error" class="error">{{ error }}</p>
    <p v-if="notice" class="ok">{{ notice }}</p>
    <p v-if="report" class="ok">
      Skaneeritud {{ report.scanned }}, indekseeritud {{ report.ingested }} ({{ report.chunks }} lõiku), muutumatuid
      {{ report.unchanged }}, eemaldatud {{ report.removed }}.
    </p>

    <div class="split">
      <div class="list-pane">
        <p v-if="loading" class="hint">Laen…</p>
        <ul v-else class="list">
          <li v-for="doc in docs" :key="doc.path">
            <button type="button" :class="{ active: selected?.path === doc.path }" @click="open(doc.path)">
              <span class="path">{{ doc.path }}</span>
              <span class="meta">
                <span class="badge" :class="doc.origin">{{ ORIGIN_LABELS[doc.origin] }}</span>
                <span>{{ doc.source }}</span>
                <span>kaal {{ doc.quality }}</span>
                <span :class="{ unindexed: !doc.chunks }">{{ doc.chunks }} lõiku</span>
              </span>
            </button>
          </li>
        </ul>
      </div>

      <div v-if="creating || selected" class="editor">
        <div v-if="editingFile" class="warn">
          See dokument on failis. Salvestamine ei muuda faili — see loob andmebaasi ülekirjutuse, mis failist ette
          läheb, kuni selle kustutad või ekspordid.
        </div>

        <label class="field">
          <span>Teekond</span>
          <input
            v-model.trim="draft.path"
            type="text"
            spellcheck="false"
            placeholder="sync/uus-teema.md"
            :disabled="!creating"
          />
          <small v-if="creating">Kaustad on teemad: sync/, directo/, shopify/, learned/, misc/.</small>
        </label>

        <div class="row">
          <label class="field">
            <span>Allikas</span>
            <input v-model.trim="draft.source" type="text" spellcheck="false" placeholder="sync-piibel" />
          </label>
          <label class="field narrow">
            <span>Keel</span>
            <input v-model.trim="draft.language" type="text" spellcheck="false" />
          </label>
          <label class="field narrow">
            <span>Kaal</span>
            <input v-model.number="draft.quality" type="number" step="0.01" min="0" max="5" />
          </label>
        </div>

        <label class="field">
          <span>Sisu</span>
          <textarea
            v-model="draft.body"
            rows="18"
            spellcheck="false"
            placeholder="# Pealkiri&#10;&#10;Sisu markdownis…"
          />
        </label>

        <details v-if="selected?.fileBody">
          <summary>Failiversioon (ülekirjutuse all)</summary>
          <pre>{{ selected.fileBody }}</pre>
        </details>

        <div class="actions">
          <button class="btn" type="button" :disabled="busy || !draft.path || !draft.body.trim()" @click="save()">
            {{ editingFile ? "Salvesta ülekirjutusena" : "Salvesta" }}
          </button>
          <button v-if="canDelete" class="btn-ghost" type="button" :disabled="busy" @click="remove()">
            {{ selected?.origin === "override" ? "Taasta failiversioon" : "Kustuta" }}
          </button>
          <button class="btn-ghost" type="button" :disabled="busy" @click="close()">Sulge</button>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.kb {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.kb-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.tools {
  display: flex;
  gap: 0.5rem;
}

.hint {
  color: var(--muted);
  font-size: 0.82rem;
  margin: 0;
  max-width: 620px;
}

code {
  background: var(--soft);
  padding: 0.05rem 0.3rem;
  border-radius: 4px;
}

.split {
  display: grid;
  grid-template-columns: minmax(240px, 340px) 1fr;
  gap: 1rem;
  align-items: start;
}

@media (max-width: 860px) {
  .split {
    grid-template-columns: 1fr;
  }
}

.list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  max-height: 70vh;
  overflow-y: auto;
}

.list button {
  width: 100%;
  text-align: left;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--paper);
  padding: 0.5rem 0.6rem;
  font: inherit;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.list button.active {
  border-color: var(--green);
}

.path {
  font-size: 0.84rem;
  font-weight: 600;
  color: var(--ink);
  word-break: break-all;
}

.meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  font-size: 0.72rem;
  color: var(--muted);
}

.badge {
  border: 1px solid var(--line);
  border-radius: 999px;
  padding: 0.02rem 0.4rem;
  font-weight: 600;
}

.badge.override {
  color: var(--gold-dark);
  border-color: var(--gold);
}

.badge.database {
  color: var(--green);
  border-color: var(--green);
}

.unindexed {
  color: var(--warn);
}

.editor {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 0.9rem 1rem;
  background: var(--paper);
}

.row {
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1 1 200px;
}

.field.narrow {
  flex: 0 0 110px;
}

.field > span {
  font-size: 0.78rem;
  font-weight: 600;
}

input,
textarea {
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 0.45rem 0.6rem;
  font: inherit;
  font-size: 0.86rem;
  background: #fff;
  color: var(--ink);
}

textarea {
  font-family: ui-monospace, "SFMono-Regular", Menlo, monospace;
  font-size: 0.8rem;
  resize: vertical;
}

input:disabled {
  background: var(--soft);
  color: var(--muted);
}

small {
  color: var(--muted);
  font-size: 0.74rem;
}

details pre {
  max-height: 260px;
  overflow: auto;
  background: var(--soft);
  border-radius: 8px;
  padding: 0.6rem;
  font-size: 0.76rem;
  white-space: pre-wrap;
}

summary {
  font-size: 0.8rem;
  color: var(--muted);
  cursor: pointer;
}

.warn,
.error,
.ok {
  margin: 0;
  font-size: 0.82rem;
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 0.5rem 0.7rem;
}

.warn {
  color: var(--gold-dark);
  border-color: var(--gold);
}

.error {
  color: var(--warn);
  border-color: var(--warn);
}

.ok {
  color: var(--green);
  border-color: var(--green);
}

.actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}
</style>
